<aside class="sidekick-panel">
  <!-- Top Header Bar using Taiga UI header & icon buttons -->
  <header class="sidekick-header" tuiHeader>
    <!-- Drawer Title -->
    <div class="header-left">
      <span class="drawer-title">Support</span>
    </div>

    <!-- Header Action Icons using Taiga UI tuiIconButton -->
    <div class="header-actions">
      <button
        tuiIconButton
        type="button"
        size="s"
        appearance="flat-grayscale"
        iconStart="@tui.external-link"
        title="Open Documentation"
        (click)="openDocumentation()">
      </button>

      <button
        tuiIconButton
        type="button"
        size="s"
        appearance="flat-grayscale"
        [iconStart]="isExpanded ? '@tui.minimize-2' : '@tui.maximize-2'"
        title="{{ isExpanded ? 'Compact View' : 'Expand View' }}"
        (click)="toggleExpand()">
      </button>

      <button
        tuiIconButton
        type="button"
        size="s"
        appearance="flat-grayscale"
        iconStart="@tui.x"
        title="Close"
        class="close-btn"
        (click)="close()">
      </button>
    </div>
  </header>

  <!-- Messages / Stream View Area matching sistem-ai-insight -->
  <tui-scrollbar class="sidekick-content" #scrollContainer>
    <div class="chat-messages-container">
      <ng-container *ngFor="let message of messages()">
        <!-- User Outgoing Bubble -->
        <span *ngIf="message.isUser || message.sender === 'user'" class="container outgoing">
          <span appearance="accent" tuiMessage class="multiline">
            <div class="message-content-wrapper">
              <span class="message-text">{{ message.text }}</span>
            </div>
          </span>
          <div class="message-actions user-message-actions">
            <button type="button" class="action-btn" (click)="copyMessage(message.text)" title="Copy">
              <tui-icon icon="@tui.copy" size="s"></tui-icon>
            </button>
          </div>
        </span>

        <!-- Thinking Indicator -->
        <div *ngIf="(!message.isUser && message.sender !== 'user') && message.isThinking" class="thinking-indicator" role="status" aria-live="polite">
          <span class="thinking-dots" aria-hidden="true">
            <span></span><span></span><span></span>
          </span>
          <span class="thinking-label">Thinking…</span>
        </div>

        <!-- Assistant Incoming Message -->
        <span *ngIf="(!message.isUser && message.sender !== 'user') && !message.isThinking" class="container incoming">
          <span tuiMessage class="multiline">
            <div class="message-content-wrapper">
              <span class="message-text" [innerHTML]="formatAiMessage(message)"></span>

              <!-- Direct Ticket Form inside Assistant View -->
              <div *ngIf="message.isTicketForm" tuiCardMedium tuiAppearance="neutral" class="embedded-ticket-box">
                <div *ngIf="ticketSuccess()" class="ticket-alert success-alert">
                  <tui-notification appearance="positive">
                    <strong>Support Ticket Created!</strong>
                    <p>We received your request and will contact you via email shortly.</p>
                  </tui-notification>
                  <button tuiButton size="s" appearance="secondary" class="mt-2" (click)="navigateToTickets()">
                    View in My Tickets
                  </button>
                </div>

                <div *ngIf="ticketError()" class="ticket-alert error-alert">
                  <tui-notification appearance="negative" (close)="ticketError.set(null)">
                    {{ ticketError() }}
                  </tui-notification>
                </div>

                <form [formGroup]="ticketForm" (ngSubmit)="submitTicket()" *ngIf="!ticketSuccess()" class="ticket-form">
                  <tui-textfield tuiChevron [tuiTextfieldCleaner]="false" class="mb-2">
                    <label tuiLabel>Category</label>
                    <input formControlName="issueType" tuiSelect />
                    <tui-data-list-wrapper *tuiTextfieldDropdown new [items]="issueCategories" />
                  </tui-textfield>

                  <tui-textfield class="mb-2">
                    <label tuiLabel>Short Title</label>
                    <input formControlName="subject" tuiTextfield placeholder="e.g. Rate card calculation discrepancy" />
                  </tui-textfield>

                  <tui-textfield class="mb-2">
                    <label tuiLabel>Message</label>
                    <textarea formControlName="message" tuiTextfield rows="4" placeholder="Describe what you need help with..."></textarea>
                  </tui-textfield>

                  <button
                    tuiButton
                    type="submit"
                    size="s"
                    appearance="primary"
                    [style.width.%]="100"
                    [disabled]="isSubmittingTicket() || ticketForm.invalid">
                    <span *ngIf="!isSubmittingTicket()">Submit Ticket</span>
                    <tui-loader *ngIf="isSubmittingTicket()" size="s" [style.color]="'white'"></tui-loader>
                  </button>
                </form>
              </div>

              <!-- Message Actions (Copy, Like, Dislike, Share) -->
              <div class="message-actions" *ngIf="!message.isTicketForm">
                <button type="button" class="action-btn" (click)="copyMessage(message.text)" title="Copy">
                  <tui-icon icon="@tui.copy" size="s"></tui-icon>
                </button>
                <button type="button" class="action-btn" [class.active]="message.isLiked"
                  (click)="likeMessage(message)" title="Like">
                  <tui-icon icon="@tui.thumbs-up" size="s"></tui-icon>
                </button>
                <button type="button" class="action-btn" [class.active]="message.isDisliked"
                  (click)="dislikeMessage(message)" title="Dislike">
                  <tui-icon icon="@tui.thumbs-down" size="s"></tui-icon>
                </button>
                <button type="button" class="action-btn" (click)="shareMessage(message.text)" title="Share">
                  <tui-icon icon="@tui.share" size="s"></tui-icon>
                </button>
              </div>
            </div>
          </span>
        </span>
      </ng-container>
    </div>
  </tui-scrollbar>

  <!-- Bottom Floating Scroll Indicator & Prompt Bar using Taiga UI components -->
  <div class="sidekick-footer">
    <button
      *ngIf="showScrollDown()"
      tuiIconButton
      type="button"
      size="xs"
      appearance="secondary-grayscale"
      iconStart="@tui.chevron-down"
      (click)="scrollToBottom()"
      title="Scroll down"
      class="scroll-down-btn"
      aria-label="Scroll down">
    </button>

    <!-- Bottom Input Capsule matching AI search bar -->
    <div class="input-capsule-wrapper">
      <tui-textfield iconStart="@tui.search" [tuiTextfieldCleaner]="false" tuiTextfieldSize="m" class="input-capsule-field">
        <input
          tuiTextfield
          [formControl]="queryControl"
          (keydown.enter)="onSend()"
          [placeholder]="isSending() ? 'Thinking…' : 'Ask anything about your parking performance...'"
          [readOnly]="isSending()"
          class="prompt-input" />

        <button
          type="button"
          class="voice-button"
          title="Voice input"
          [disabled]="isSending()"
          (click)="onVoiceInput()">
          <tui-avatar src="@tui.mic" size="m" appearance="icon" />
        </button>

        <button
          type="button"
          class="send-button"
          [class.sending]="isSending()"
          [disabled]="isSending()"
          (click)="onSend()"
          title="Send query"
          aria-label="Send">
          <tui-avatar src="@tui.arrow-up" size="s" appearance="primary" />
          <span class="listen-bars" *ngIf="isSending()" aria-hidden="true">
            <span></span><span></span><span></span>
          </span>
        </button>
      </tui-textfield>

      <div class="input-des">
        Insights and forecasts come from live operational data and recent patterns. Forecasts are estimates.
      </div>
    </div>
  </div>
</aside>
